import { tz } from '@date-fns/tz';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';

import { filterEncoders, serializeFilters } from '@utils/serialize-filters';

import type {
  ImpactedAppVersionsFilters,
  VulnerabilityDetail,
  VulnerabilityLookupItem,
} from './quick-search.models';

/**
 * Picks the record used to represent a lookup result. The NVD provider is
 * preferred for its canonical scoring; otherwise the first available provider.
 */
export const getPrimaryDetail = ({
  details,
}: VulnerabilityLookupItem): VulnerabilityDetail | undefined =>
  details?.find(detail => detail.provider === 'nvd') ?? details?.[0];

/**
 * Formats the vulnerability database build date as an " as of <date> UTC"
 * suffix, or an empty string when it is missing or unparseable.
 */
export const builtAtSuffix = (builtAt: string | null | undefined): string => {
  // Offsetless build dates are stored as UTC; parseISO with a UTC context
  // avoids reading them as local time, and the guard stops format throwing on
  // an unparseable value.
  const date = builtAt ? parseISO(builtAt, { in: tz('UTC') }) : null;

  return date && !Number.isNaN(date.valueOf())
    ? ` as of ${format(date, 'yyyy-MM-dd HH:mm')} UTC`
    : '';
};

/**
 * Message for a lookup that yields no usable vulnerability — zero matches or an
 * ambiguous multi-match. Anchored to the vulnerability database's build date so
 * it reads as "no data as of <build>" rather than implying a live answer.
 */
export const noVulnerabilityDataMessage = (
  vulnerabilityId: string,
  builtAt: string | null | undefined,
): JSX.Element => (
  <>
    No vulnerability data found for &quot;<strong>{vulnerabilityId}</strong>
    &quot;{builtAtSuffix(builtAt)}
  </>
);

/**
 * Message for a vulnerability that matched but affects no application version.
 * Anchored to the vulnerability database's build date so it reads as "no
 * findings as of <build>".
 */
export const noImpactedVersionsMessage = (
  builtAt: string | null | undefined,
): string =>
  `We could not find this vulnerability in any of your application versions${builtAtSuffix(
    builtAt,
  )}`;

/**
 * Translates impacted-app-versions filter state into the query params consumed
 * by the Node service wrapper.
 */
export const serializeImpactedAppVersionsFilters = (
  filters?: ImpactedAppVersionsFilters,
) =>
  serializeFilters(filters, {
    applicationName: filterEncoders.string,
    versionName: filterEncoders.string,
    contactName: filterEncoders.string,
    contactEmail: filterEncoders.string,
    status: filterEncoders.stringArray,
    releaseDate: filterEncoders.dateRange('releaseDate'),
    hasFix: filterEncoders.boolean,
    affectedPackagesMin: filterEncoders.number,
    affectedPackagesMax: filterEncoders.number,
    annotationStatus: filterEncoders.stringArray,
  });
